import {Component} from "@angular/core";
import {Subscription} from "rxjs";
import {QueueService} from "../../../services/queue.service";
import {DisplayTicketRequest} from "../../../model/queue/display-ticket-request.model";
import {TicketDTO} from "../../../model/queue/ticketDTO.model";
import {NgClass} from "@angular/common";
import {environment} from "../../../../environments/environment";
import * as QRCode from "qrcode";

@Component({
    selector: 'app-queue-display',
    templateUrl: './queue-display.component.html',
    styleUrls: ['./queue-display.component.scss'],
    imports: [
        NgClass
    ]
})

export class QueueDisplayComponent {
    currentTicket: TicketDTO | null = null;
    waitingList: TicketDTO[] = [];
    joinQueueUrl = '';
    joinQueueQrUrl = '';

    eventSub: Subscription;

    isFlashing = false;
    currentTicketAnimationClass = '';
    outgoingTicketNumber: number | null = null;
    outgoingAnimationActive = false;
    waitingItemAnimationMap: Record<number, 'reordered'> = {};

    private lastTicketId: number;
    private animationCleanupTimers: ReturnType<typeof setTimeout>[] = [];

    constructor(private queueService: QueueService) {

    }

    ngOnInit(): void {
        this.initializeJoinQueueQr();
        this.initWaitList(false);

        this.eventSub = this.queueService.getServerSentEvents().subscribe({
            next: (data: DisplayTicketRequest) => {
                this.applyDisplayState(data, true);
            },
            error: () => {
                console.log("Erreur SSE");
            }
        });
    }

    ngOnDestroy(): void {
        if (this.eventSub) {
            this.eventSub.unsubscribe();
        }
        this.animationCleanupTimers.forEach(timer => clearTimeout(timer));
    }

    initWaitList(animate: boolean) {
        this.queueService.getServingTicketAndWaitingTicket().subscribe((data) => {
            this.applyDisplayState(data, animate);
        })
    }

    triggerAlert() {
        this.isFlashing = true;
        const timer = setTimeout(() => this.isFlashing = false, 3000);
        this.animationCleanupTimers.push(timer);
    }

    getWaitingItemClass(ticket: TicketDTO, index: number): Record<string, boolean> {
        return {
            next: index === 0,
            'reorder-animation': this.waitingItemAnimationMap[ticket.id] === 'reordered'
        };
    }

    private applyDisplayState(data: DisplayTicketRequest, animate: boolean): void {
        const previousCurrent = this.currentTicket;
        const previousWaiting = this.waitingList ?? [];
        const previousWaitingIds = new Set(previousWaiting.map(ticket => ticket.id));

        const nextCurrent = data.servingTicket ?? null;
        const nextWaiting = data.waitingTickets ?? [];

        const hasCurrentChanged = previousCurrent?.id !== nextCurrent?.id;
        const promotedFromWaiting = !!(nextCurrent && previousWaitingIds.has(nextCurrent.id));
        const removedFromCurrent = !!(previousCurrent && (!nextCurrent || previousCurrent.id !== nextCurrent.id));

        this.currentTicket = nextCurrent;
        this.waitingList = nextWaiting;

        if (nextCurrent && nextCurrent.id !== this.lastTicketId) {
            this.lastTicketId = nextCurrent.id;
            this.triggerAlert();
        }

        if (!animate) {
            this.currentTicketAnimationClass = '';
            this.outgoingAnimationActive = false;
            this.waitingItemAnimationMap = {};
            return;
        }

        this.currentTicketAnimationClass = '';
        if (promotedFromWaiting && hasCurrentChanged) {
            this.currentTicketAnimationClass = 'promoted-in';
            this.clearCurrentTicketAnimationLater();
        }

        if (removedFromCurrent) {
            this.outgoingTicketNumber = previousCurrent.number;
            this.outgoingAnimationActive = true;
            const timer = setTimeout(() => {
                this.outgoingAnimationActive = false;
                this.outgoingTicketNumber = null;
            }, 900);
            this.animationCleanupTimers.push(timer);
        }

        this.waitingItemAnimationMap = {};
        if (promotedFromWaiting || removedFromCurrent) {
            const nextWaitingIds = new Set(nextWaiting.map(ticket => ticket.id));
            nextWaiting.forEach(ticket => {
                if (previousWaitingIds.has(ticket.id) && nextWaitingIds.has(ticket.id)) {
                    this.waitingItemAnimationMap[ticket.id] = 'reordered';
                }
            });
        }

        if (Object.keys(this.waitingItemAnimationMap).length > 0) {
            this.clearWaitingReorderAnimationsLater();
        }
    }

    private clearCurrentTicketAnimationLater(): void {
        const timer = setTimeout(() => {
            this.currentTicketAnimationClass = '';
        }, 900);
        this.animationCleanupTimers.push(timer);
    }

    private clearWaitingReorderAnimationsLater(): void {
        const timer = setTimeout(() => {
            this.waitingItemAnimationMap = {};
        }, 700);
        this.animationCleanupTimers.push(timer);
    }

    private initializeJoinQueueQr(): void {
        const baseUrl = this.resolveFrontendBaseUrl();
        this.joinQueueUrl = `${baseUrl}/queue/join`;
        QRCode.toDataURL(this.joinQueueUrl, {
            width: 220,
            margin: 1
        })
            .then((dataUrl) => {
                this.joinQueueQrUrl = dataUrl;
            })
            .catch(() => {
                this.joinQueueQrUrl = '';
            });
    }

    private resolveFrontendBaseUrl(): string {
        try {
            const configured = (environment as any).FRONTEND_URL || environment.API_URL || '';
            const normalized = new URL(configured, window.location.origin);

            const isLikelyBackendPort = normalized.hostname === window.location.hostname && normalized.port === '8080';
            const hasApiPath = normalized.pathname && normalized.pathname !== '/' && normalized.pathname.toLowerCase().includes('api');

            if (isLikelyBackendPort || hasApiPath) {
                return window.location.origin;
            }
            return `${normalized.protocol}//${normalized.host}`;
        } catch {
            return window.location.origin;
        }
    }
}
